// src\app\pages\companiesHouse\pageFunctions\commonFunctions.js

import HOOK from "../../../components/hooks"
import { CHECKBOX } from "../../../modules/checkbox_constants"
import { BASE, KIND, LSE_INDEX_ENUM, PATH, RADIO, SCOPE, TAB, URL_SOURCE } from "../../../modules/constants"
import { deepEqual } from "../../../modules/utilities"

export const fetchId = ids => {
  // Returns 1st id if only one or either 1st CH id or 1st PSC id
  // NB: ids may constist of: a static (from GS) or non-static GUID (32 char) id
  // and/or one or more 27 char CH ids
  // and/or one or more PSC ids (...@.....)
  return ids.length === 1
    ? ids[0]
    : getBestId(ids)
}

const getBestId = ids => {
  // Returns either 1st CH id or 1st PSC id
  const idLen27 = ids.find(element => element.length === 27) // CH officer id
  const idLenNot32 = ids.find(element => element.length !== 32) // PSC id
  return idLen27 || idLenNot32
}

export const validateTableInfoPage = ({ rowsPerPage, page, count }) => {
  // If a change to the search or filters results in a 'row start'
  // higher than the item's length, we must modify it accordingly.
  // Returns tableInfo object
  let lastPage = Math.floor((count - 1) / rowsPerPage)
  if (lastPage < 0) lastPage = 0

  // console.log("count: " + count + ", page: " + page + ", last page: " + lastPage)
  return (page > lastPage)
    ? { rowsPerPage, page: lastPage }
    : { rowsPerPage, page }
}

export const analyseUrl = props => {
  // console.log('analyseUrl props:', props)

  const search = analyseSearch(props)
  const urlSource = search.urlSource

  const newProps = {
    currentLocation: window.location,
    searchStringObj: { ...props.searchStringObj },
    ...search,
    ...analysePathname({ ...props, urlSource }),
  }

  // NB analyseSearch(props) must be call before this switch so that newProps.searchString is current
  newProps.searchStringObj[newProps.tab.INDEX] = newProps.searchString || ''
  return newProps
}

export const newUrl = (props, urlProps) => {
  // Note that if we change from person to company tab, only the tab index
  // ("/company/" or "/person/" in url) will have changed so we must test for this
  // console.log('deepEqual(props.checkboxes, urlProps.checkboxes):',deepEqual(props.checkboxes, urlProps.checkboxes))
  return props.companyId !== urlProps.companyId
    || !deepEqual(props.checkboxes, urlProps.checkboxes)
    || props.nexusView !== urlProps.nexusView
    || props.personId !== urlProps.personId
    || !deepEqual(props.radio, urlProps.radio)
    || props.tabIndex !== urlProps.tab.INDEX
    || JSON.stringify(props.searchStringObj) !== JSON.stringify(urlProps.searchStringObj)
}

const analyseSearch = props => {
  // console.log('analyseSearch props:', props)
  // Extract the components from the location.search string
  let params = new URLSearchParams(window.location.search.replace("&amp;", "%26"))
  const urlCheckboxes = getCheckboxes(params, props.features)
  const previousCheckboxes = getPreviousCheckboxes({ ...props, urlCheckboxes })
  const view = params.get("view")
  return {
    checkboxes: urlCheckboxes,
    companyName: params.get("company"),
    nexusView: getNexusView(view, props.nexusView),
    personName: params.get("person"),
    previousCheckboxes,
    radio: getRadioFromUrl(params.get("radio"), props.radio),
    scope: getScope(params.get("scope"), props.scope),
    searchString: params.get("search-string"),
    seed: getSeed(params.get("seed")),
    urlSource: params.get("source") || URL_SOURCE.NUMERABLE,
  }
}

const analysePathname = props => {
  // console.log('analysePathname props:', props)
  const pathnameSplit = window.location.pathname.split('/')
  let { companyId, features, newsId, personId, urlSource } = props
  let uriId = pathnameSplit[2]
  const path = pathnameSplit[1]

  return { companyId, newsId, personId, uriId, ...switchOnPath({ features, path, uriId, urlSource }) }
}

// Returns the correspoding TAB (and company, person or news id) for url, taking user features into account
// Eg 1 '../company/09741232/...' returns {tab: TAB[BASE.COMPANY], companyId: 09741232}
// Eg 2 If not looged in, '.../dashboard/...' returns { tab: TAB[BASE.SEARCH] }
const switchOnPath = props => {
  // console.log('switchOnPath props:', props)
  const { features, path, uriId, urlSource } = props
  switch (path) {
    // If path is search, company or person tab, we can go to these even if not logged in
    case PATH[BASE.COMPANY]:
      return { tab: TAB[BASE.COMPANY], companyId: uriId }

    case PATH[BASE.PERSON]:
      return { tab: TAB[BASE.PERSON], personId: uriId }

    case PATH[BASE.SEARCH]:
      return { tab: TAB[BASE.SEARCH] }

    default:
      if (features?.Favourites || urlSource === URL_SOURCE.CLUB350)
        // If path is favourites page, we must be logged in, but don't require premium
        switch (path) {
          case PATH[BASE.FAVOURITES]:
            return { tab: TAB[BASE.FAVOURITES] }

          // For any LSE page, we require premium or 350 Club access
          default:
            if (features?.MaxLseData)
              switch (path) {
                case PATH[BASE.LSE.ADVISERS]:
                  return { tab: TAB[BASE.LSE.ADVISERS] }

                case PATH[BASE.LSE350]:
                case PATH[BASE.LSE.COMPANIES]:
                  return { tab: TAB[BASE.LSE.COMPANIES] }

                case PATH[BASE.LSE.ROLES]:
                  return { tab: TAB[BASE.LSE.ROLES] }

                case PATH[BASE.LSE.PEOPLE]:
                  return { tab: TAB[BASE.LSE.PEOPLE] }

                case PATH[BASE.LSE.NEWS]:
                  return { tab: TAB[BASE.LSE.NEWS] }

                case PATH[BASE.NEWS_RELEASE]:
                  return { tab: TAB[BASE.NEWS_RELEASE], newsId: uriId }

                // The default for users with lse data is the dashboard
                default:
                  return { tab: TAB[BASE.DASHBOARD] }
              }
        }
      // Default is search tab
      return { tab: TAB[BASE.SEARCH] }
  }
}

const getColumn = (view, exponent) => {
  let c = Math.trunc(view / 10 ** exponent) % 10
  // console.log(`a:${a} b:${b} = ${c}`)
  return c
}

const getNexusView = (newView, defaultView) => {
  // Convert string 'newView' in to an int
  // NB: i will be NaN if newView === null)
  const i = parseInt(newView, 10)
  return isNaN(i) || i < 0
    ? defaultView
    : getColumn(i, 0)
}

const getRadioFromUrl = (radioStr, defaultRadio) => {
  // Convert string 'radio' in to an int
  // NB: i will be NaN if radio === null)
  const i = parseInt(radioStr, 10)

  var radio = {}

  if (!isNaN(i)) {
    Object.keys(RADIO).forEach((key, index) => {
      // console.log('key:', index, ' ', key)
      radio[key] = getColumn(i, index)
    })
  }

  return isNaN(i) || i < 0
    ? defaultRadio
    : radio
}

// Get the checkbox values from the url
const getCheckboxes = (params, features) => {
  const checkboxes = {}
  for (const [_, hook] of Object.entries(HOOK)) {
    let checkbox = getIntParam({ tag: hook, params })

    // Only add the property if there is a url, or default value
    if (!isNaN(checkbox))
      checkboxes[hook] = checkbox
  }

  // Trial users are locked to FTSE 100 within the LSE Group selector - clamp here too, so a
  // hand-edited 'indx' url parameter can't be used to restore the full index range
  if (features?.Trial && checkboxes[HOOK.LSE_INDEX] !== undefined)
    checkboxes[HOOK.LSE_INDEX] = checkboxes[HOOK.LSE_INDEX] & CHECKBOX[HOOK.LSE_INDEX][LSE_INDEX_ENUM.FTSE_100].BIT

  // console.log('checkboxes:', checkboxes)
  return checkboxes
}

const getIntParam = ({ tag, params }) => {
  let number = parseInt(params.get(tag))
  return isNaN(number) ? CHECKBOX.DEFAULTS[tag] : number
}

// If new (url) checkboxes is different from the props (current) checkboxes,
// return currentCheckboxes (checkboxes). Else return previousCheckboxes
const getPreviousCheckboxes = props => {
  // console.log('getPreviousCheckboxes props:',props)
  const { urlCheckboxes, checkboxes, previousCheckboxes } = props
  return (deepEqual(urlCheckboxes, checkboxes))
    ? previousCheckboxes
    : checkboxes
}

// Returns the KIND value given tabIndex and radio
export const getKind = (tabIndex, radio) => {
  // console.log('tabIndex:', tabIndex, ' radio:', radio)
  switch (tabIndex) {
    //#region LSE tabs
    case TAB[BASE.DASHBOARD].INDEX:
      return KIND.DASHBOARD

    case TAB[BASE.SEARCH].INDEX:
      return KIND.SEARCH.COMPANIES

    case TAB[BASE.LSE.COMPANIES].INDEX:
      switch (radio[BASE.LSE.COMPANIES]) {

        case RADIO[BASE.LSE.COMPANIES].OVERVIEW:
          return KIND.LSE.COMPANIES.OVERVIEW

        case RADIO[BASE.LSE.COMPANIES].BOARD:
          return KIND.LSE.COMPANIES.BOARD

        case RADIO[BASE.LSE.COMPANIES].GOVERNANCE:
          return KIND.LSE.COMPANIES.GOVERNANCE

        case RADIO[BASE.LSE.COMPANIES].DATES:
          return KIND.LSE.COMPANIES.DATES

        case RADIO[BASE.LSE.COMPANIES].ADVISERS:
          return KIND.LSE.COMPANIES.ADVISERS

        case RADIO[BASE.LSE.COMPANIES].COMMITTEES:
          return KIND.LSE.COMPANIES.COMMITTEES
      }

    case TAB[BASE.LSE.ROLES].INDEX:
      switch (radio[BASE.LSE.ROLES]) {
        case RADIO[BASE.LSE.ROLES].BOARD:
          return KIND.LSE.ROLES.BOARD

        case RADIO[BASE.LSE.ROLES].COMMITTEES:
          return KIND.LSE.ROLES.COMMITTEES
      }

    case TAB[BASE.LSE.PEOPLE].INDEX:
      switch (radio[BASE.LSE.PEOPLE]) {
        case RADIO[BASE.LSE.PEOPLE].BOARD:
          return KIND.LSE.PEOPLE.BOARD

        case RADIO[BASE.LSE.PEOPLE].COMMITTEES:
          return KIND.LSE.PEOPLE.COMMITTEES
      }

    case TAB[BASE.LSE.NEWS].INDEX:
      return KIND.LSE.NEWS

    case TAB[BASE.LSE.ADVISERS].INDEX:
      return KIND.LSE.ADVISERS

    case TAB[BASE.COMPANY].INDEX:
      switch (radio[BASE.COMPANY]) {
        case RADIO[BASE.COMPANY].DASHBOARD:
          return KIND.NEXUS.COMPANY.DASHBOARD

        case RADIO[BASE.COMPANY].BOARD:
          return KIND.NEXUS.COMPANY.BOARD

        case RADIO[BASE.COMPANY].COMMITTEES:
          return KIND.NEXUS.COMPANY.COMMITTEES

        case RADIO[BASE.COMPANY].NEWS:
          return KIND.NEXUS.COMPANY.NEWS
        case RADIO[BASE.COMPANY].ADVISER_CLIENTS:
          return KIND.NEXUS.COMPANY.ADVISER_CLIENTS
        case RADIO[BASE.COMPANY].CONNECTIONS:
          return KIND.NEXUS.COMPANY.CONNECTIONS
      }
    //#endregion LSE tabs

    //#region nexus tabs
    case TAB[BASE.PERSON].INDEX:
      switch (radio[BASE.PERSON]) {
        case RADIO[BASE.PERSON].ROLES:
          return KIND.NEXUS.PERSON.ROLES

        case RADIO[BASE.PERSON].COMMITTEES:
          return KIND.NEXUS.PERSON.COMMITTEES

        case RADIO[BASE.PERSON].NEWS:
          return KIND.NEXUS.PERSON.NEWS

        case RADIO[BASE.PERSON].CONNECTIONS:
          return KIND.NEXUS.PERSON.CONNECTIONS
      }

    case TAB[BASE.NEWS_RELEASE].INDEX:
      return KIND.NEWS_RELEASE

    case TAB[BASE.FAVOURITES].INDEX:
      switch (radio[BASE.FAVOURITES]) {
        case RADIO[BASE.FAVOURITES].PEOPLE:
          return KIND.FAVOURITE.PEOPLE

        case RADIO[BASE.FAVOURITES].COMPANIES:
          return KIND.FAVOURITE.COMPANIES
      }
    //#endregion nexus tabs
    default:
      return KIND.SEARCH.COMPANIES
  }
}

// Return the name of the KIND from value kind
// Primarily used for dubugging
export const findKind = kind => {
  for (const prop1 in KIND) {
    if (typeof KIND[prop1] === "object") {
      for (const prop2 in KIND[prop1]) {
        if (typeof KIND[prop1][prop2] === "object") {
          for (const prop3 in KIND[prop1][prop2])
            if (kind === KIND[prop1][prop2][prop3])
              return `${prop1}.${prop2}.${prop3}`
        }
        if (kind === KIND[prop1][prop2])
          return `${prop1}.${prop2}`
      }
    } if (kind === KIND[prop1])
      return `${prop1}`
  }
}

/*
const validateCheckbox = (checkboxes, features) => {

  if (!features.StandardCheckboxes) {
    checkboxes = setCheckboxDefault(checkboxes, CHECKBOX[HOOK.TENSE])
    checkboxes = setCheckboxDefault(checkboxes, CHECKBOX[HOOK.CONNECTIONS])
    checkboxes = setCheckboxDefault(checkboxes, CHECKBOX[HOOK.SEX])
    checkboxes = setCheckboxDefault(checkboxes, CHECKBOX[HOOK.POSITION])
    checkboxes = setCheckboxDefault(checkboxes, CHECKBOX[HOOK.INDUSTRY])
    checkboxes = setCheckboxDefault(checkboxes, CHECKBOX[HOOK.NEWS_CATEGORY])
  }

  return checkboxes
}

const setCheckboxDefault = (checkboxes, sectorDefaults) => {
  sectorDefaults.forEach(c => {
    // NB: Can't use 'checkbox & (~p.BIT)' because we're using 64bit.
    // So, set all, then unset - using '-' - the relevant bits
    checkboxes = BITWISE.or(checkboxes, c.BIT)

    if (c.UNCHECKED)
      checkboxes -= c.BIT // Unset the relevant bit
  })

  return checkboxes
}
*/

// Get search scope
const getScope = (paramsScope, propsScope) =>
  Object.keys(SCOPE).some(s => SCOPE[s] === paramsScope)
    ? paramsScope
    : propsScope

const getSeed = seed => seed || Math.floor(Math.random() * 1000000).toString()

